// "Which wave?" — the page behind the Pick Queue's featured Pick & Sort
// card once a warehouse has a put wall.
//
// A put wall wave is a Pick & Sort wave sorted on the 72-slot wall
// instead of a bay, and it only takes orders that fit whole into one
// tote. Those orders are held OUT of the normal Sorting pool, so the
// card can no longer just start a wave: the picker chooses here.
//
//   • Put Wall — the tote-sized pool. Disabled (with the reason) when empty.
//   • Sorting  — today's Pick & Sort wave, with the Small/Large size pills
//                and an "Include put wall orders" box that sweeps the
//                tote-sized pool back in. The box's default is decided on
//                page load (ticked while the put wall pool is under the
//                warehouse's minimum) and is never persisted; toggling it
//                re-polls the selector so the Sorting counts track the box.
//
// Same shell, filter controls, cards and poll as the Pick Queue. The
// filters the picker had set on the queue arrive via navigation state
// and open here already selected; a direct load falls back to the same
// localStorage the queue reads. They are live: a change re-polls both
// cards' counts and is written back to the shared keys, so Back shows
// the queue with the updated selection. On a successful start we hand
// over to /picking, which mounts ActiveWaveScreen off /picking/active
// (exactly as Pick to Tote does).
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Grid3x3, Layers } from "lucide-react";
import { toast } from "react-toastify";
import { usePageTitle } from "../hooks/usePageTitle";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { PICKING_POLL_MS } from "../components/picking/constants";
import {
    buildSelectorUrl, persistPickFilters, persistStoredWaveSize, readStoredWaveSize, resolveWaveSize,
    WaveStartingOverlay,
    type PasLargeInfo, type PickFilters, type PutWallInfo, type SortStation,
} from "../components/picking/selectorShared";
import {
    usePickFilters, TierToggle, SelectorFilterRows, EMPTY_AGE_COUNTS, EMPTY_DELIVERY_COUNTS,
    type AgeCounts, type ChannelCount, type DeliveryCounts,
} from "../components/picking/SelectorFilters";
import "../styles/picking.css";

interface ChooserSelectorResponse {
    counts?: { pick_and_sort?: number };
    order_counts?: { pick_and_sort?: number };
    // Everything ready for Sorting under the active filters (not just the
    // next wave's cap) — the "Ready:" line, as on the queue's card.
    pool_counts?: { pick_and_sort?: { items: number; orders: number } };
    age_counts?: AgeCounts;
    delivery_counts?: DeliveryCounts;
    due_count?: number;
    channel_counts?: Record<string, ChannelCount>;
    pick_and_sort_large?: PasLargeInfo;
    put_wall?: PutWallInfo;
}

// The queue passes its live filter set in navigation state. Only the
// fields it actually carries (validated) seed the state; anything else
// falls back inside usePickFilters to the same storage the queue reads.
const initialFiltersFrom = (state: unknown): Partial<PickFilters> => {
    const s = (state && typeof state === "object" ? state : {}) as Partial<PickFilters>;
    const out: Partial<PickFilters> = {};
    if (s.tier === "high" || s.tier === "low") out.tier = s.tier;
    if (s.ageFilter === "all" || s.ageFilter === "<1" || s.ageFilter === "1-2" || s.ageFilter === "2+" || s.ageFilter === "custom") out.ageFilter = s.ageFilter;
    if (s.deliveryFilter === "all" || s.deliveryFilter === "express" || s.deliveryFilter === "standard") out.deliveryFilter = s.deliveryFilter;
    if (typeof s.channelFilter === "string" && s.channelFilter) out.channelFilter = s.channelFilter;
    if (typeof s.ageFrom === "string") out.ageFrom = s.ageFrom;
    if (typeof s.ageTo === "string") out.ageTo = s.ageTo;
    if (typeof s.dueFilter === "boolean") out.dueFilter = s.dueFilter;
    return out;
};

const PickingChooseWave = () => {
    usePageTitle("picking_wave_chooser_title");
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    // Seeded once from the queue's hand-over (a later state change never
    // re-seeds — the controls here own the filters from then on).
    const [initialFilters] = useState(() => initialFiltersFrom(location.state));
    const f = usePickFilters(initialFilters);
    const { filters, setChannelFilter } = f;
    const { channelFilter } = filters;
    // Write the handed-over set to the shared keys straight away, so Back
    // shows the queue with exactly this selection even if nothing here is
    // touched (every control's setter persists its own change after that).
    useEffect(() => { persistPickFilters(initialFilters); }, [initialFilters]);

    const [putWall, setPutWall] = useState<PutWallInfo | null>(null);
    const [sortingItems, setSortingItems] = useState(0);
    // null = the BE didn't send order_counts (older bundle) → row hidden.
    const [sortingOrders, setSortingOrders] = useState<number | null>(null);
    const [sortingPool, setSortingPool] = useState<{ items: number; orders: number } | null>(null);
    const [pasLarge, setPasLarge] = useState<PasLargeInfo | null>(null);
    const [ageCounts, setAgeCounts] = useState<AgeCounts>(EMPTY_AGE_COUNTS);
    const [deliveryCounts, setDeliveryCounts] = useState<DeliveryCounts>(EMPTY_DELIVERY_COUNTS);
    const [channelCounts, setChannelCounts] = useState<Record<string, ChannelCount>>({});
    const [dueCount, setDueCount] = useState<number>(0);
    // null until the first poll decides the default from the threshold.
    const [includePutWall, setIncludePutWall] = useState<boolean | null>(null);
    const [waveSizeChoice, setWaveSizeChoice] = useState<"small" | "large">(readStoredWaveSize);
    const [starting, setStarting] = useState(false);
    const busy = starting;

    const waveSize = resolveWaveSize(pasLarge, waveSizeChoice);
    const persistWaveSize = useCallback((next: "small" | "large") => {
        setWaveSizeChoice(next);
        persistStoredWaveSize(next);
    }, []);

    const selectorUrl = buildSelectorUrl(filters, waveSize, includePutWall === true);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const res = await apiFetch(selectorUrl, {});
                if (!res.ok || !alive) return;
                const body: ChooserSelectorResponse = await res.json();
                if (!alive) return;
                const wall = body.put_wall && body.put_wall.enabled === true ? body.put_wall : null;
                if (!wall) {
                    // Feature off (setting, bay, or an older BE): nothing to
                    // choose — the queue's card starts waves directly.
                    navigate("/picking", { replace: true });
                    return;
                }
                setPutWall(wall);
                setSortingItems(typeof body.counts?.pick_and_sort === "number" ? body.counts.pick_and_sort : 0);
                setSortingOrders(typeof body.order_counts?.pick_and_sort === "number" ? body.order_counts.pick_and_sort : null);
                {
                    const pool = body.pool_counts?.pick_and_sort;
                    setSortingPool(pool && typeof pool.items === "number" && typeof pool.orders === "number"
                        ? { items: pool.items, orders: pool.orders }
                        : null);
                }
                setPasLarge(body.pick_and_sort_large ?? null);
                setAgeCounts(body.age_counts ?? EMPTY_AGE_COUNTS);
                setDeliveryCounts(body.delivery_counts ?? EMPTY_DELIVERY_COUNTS);
                setDueCount(typeof body.due_count === "number" ? body.due_count : 0);
                if (body.channel_counts && typeof body.channel_counts === "object") {
                    setChannelCounts(body.channel_counts);
                    if (channelFilter !== "all"
                        && !Object.prototype.hasOwnProperty.call(body.channel_counts, channelFilter)) {
                        setChannelFilter("all");
                    }
                } else {
                    // Older backends omit this field. Keep the stored selection
                    // untouched so a rolling deployment behaves as before.
                    setChannelCounts({});
                }
                // Decide the include default ONCE, on load: a thin put wall
                // pool (under the warehouse minimum) is swept into the
                // Sorting wave by default rather than left to sit until it
                // grows. Never persisted.
                setIncludePutWall(prev => (prev === null ? wall.orders < wall.min_orders : prev));
            } catch { /* transient — next poll retries */ }
        };
        tick();
        const id = window.setInterval(tick, PICKING_POLL_MS);
        return () => { alive = false; window.clearInterval(id); };
    }, [selectorUrl, channelFilter, setChannelFilter, navigate]);

    const startWave = useCallback(async (sortStation: SortStation) => {
        if (starting) return;
        setStarting(true);
        // Keep the overlay up through the hand-over to /picking so the
        // picker never sees this page flash back before the scan screen.
        let handedOff = false;
        try {
            const res = await apiFetch("/picking/wave/start", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    pickType: "pick_and_sort",
                    tier: filters.tier,
                    ageFilter: filters.ageFilter,
                    deliveryFilter: filters.deliveryFilter,
                    channelFilter: filters.channelFilter,
                    ageFrom: filters.ageFrom || null,
                    ageTo:   filters.ageTo   || null,
                    dueFilter: filters.dueFilter,
                    // waveSize is a bay-station concept — never sent for
                    // a put wall wave.
                    waveSize: sortStation === "put_wall" ? undefined : waveSize,
                    sortStation,
                    // Ignored by the BE for a put_wall station; sent
                    // explicitly so the payload says what was asked.
                    includePutWall: sortStation === "pick_and_sort" ? includePutWall === true : false,
                }),
            });
            if (res.ok) {
                handedOff = true;
                navigate("/picking", { replace: true });
                return;
            }
            const errBody = await res.json().catch(() => ({}));
            const code = errBody?.code as string | undefined;
            if (code === "ALREADY_ACTIVE") {
                handedOff = true;
                toast.info(t("picking_already_active_wave"), { toastId: "picking-already-active" });
                navigate("/picking", { replace: true });
                return;
            }
            if (code === "NO_CANDIDATES" || code === "NO_MATCHING" || code === "NO_SELECTED") {
                toast.info(t("picking_no_orders_to_pick"), { toastId: "picking-no-orders" });
                return;
            }
            if (code === "BUILD_CONFLICT") {
                toast.error(t("picking_start_conflict"), { toastId: "picking-start-conflict" });
                return;
            }
            if (code === "PS_SMALL_BLOCKED") {
                toast.info(t("picking_small_blocked"), { toastId: "picking-small-blocked" });
                return;
            }
            if (code === "PS_LARGE_TOO_FEW") {
                toast.info(t("picking_large_too_few"), { toastId: "picking-large-too-few" });
                return;
            }
            if (code === "PS_LARGE_UNAVAILABLE") {
                toast.info(t("picking_large_wave_unavailable"), { toastId: "picking-large-unavailable" });
                return;
            }
            // Put wall races: the setting / bay went away, or the last
            // tote-sized order was swept into a Sorting wave, between the
            // poll and the tap. The next poll refreshes (or redirects).
            if (code === "PW_UNAVAILABLE") {
                toast.info(t("picking_put_wall_unavailable"), { toastId: "picking-put-wall-unavailable" });
                return;
            }
            if (code === "PW_TOO_FEW") {
                toast.info(t("picking_put_wall_too_few"), { toastId: "picking-put-wall-too-few" });
                return;
            }
            toast.error(t("picking_start_failed"), { toastId: "picking-start-fail" });
        } catch {
            toast.error(t("picking_start_failed"), { toastId: "picking-start-net" });
        } finally {
            if (!handedOff) setStarting(false);
        }
    }, [starting, filters, waveSize, includePutWall, navigate, t]);

    const putWallEmpty = !putWall || putWall.orders === 0;
    const sortingEmpty = sortingItems === 0;

    return (
        <div className="picking-selector picking-choose">
            <div className="page-title-bar picking-selector-header">
                <h1 className="page-title">{t("picking_wave_chooser_title")}</h1>
                <TierToggle tier={f.tier} onChange={f.setTier} busy={busy} />
                <button
                    type="button"
                    className="page-back-btn"
                    onClick={() => navigate("/picking")}
                    disabled={busy}
                    aria-label={t("picking_queue_back")}
                >
                    <ArrowLeft size={20} />
                </button>
            </div>

            {/* The queue's filter rows, live: a change re-polls both cards
                and persists, so Back shows the queue with the same set. */}
            <SelectorFilterRows f={f} counts={{ ageCounts, deliveryCounts, channelCounts, dueCount }} busy={busy} />

            {/* Two identical compact tiles stacked under the filters — a
                single-column layout (header / NEXT WAVE stats / ready
                line / footer) sized to its content so nothing can overlap
                on a short phone. Putwall is orange, Sorting the same tile in
                white. The tap area is its own button so the Sorting footer's
                pills and include box aren't nested inside it; Putwall's
                footer stays empty and collapses. */}
            <div className="picking-choose-grid">
                <div
                    data-testid="choose-put-wall"
                    className={`picking-choose-tile${putWallEmpty ? " picking-choose-tile--disabled" : ""}`}
                >
                    <button
                        type="button"
                        data-testid="choose-put-wall-tap"
                        className="picking-choose-tap"
                        disabled={busy || putWallEmpty}
                        onClick={() => !busy && !putWallEmpty && startWave("put_wall")}
                    >
                        <div className="picking-choose-head">
                            <span className="picking-choose-icon"><Grid3x3 size={28} strokeWidth={1.8} aria-hidden="true" /></span>
                            <span className="picking-choose-name">{t("picking_wave_chooser_put_wall")}</span>
                        </div>
                        <div className="picking-choose-stats" data-testid="put-wall-next">
                            <div className="picking-choose-caption">{t("picking_card_next_wave")}</div>
                            <div className="picking-choose-stat">
                                <span className="picking-choose-stat-num">{putWall?.next_items ?? 0}</span>
                                <span className="picking-choose-stat-label">{t("picking_card_items")}</span>
                            </div>
                            <div className="picking-choose-stat">
                                <span className="picking-choose-stat-num">{putWall?.next_orders ?? 0}</span>
                                <span className="picking-choose-stat-label">{t("picking_card_orders")}</span>
                            </div>
                        </div>
                        {putWall && putWall.orders > 0 ? (
                            <div className="picking-choose-ready" data-testid="put-wall-pool">
                                <span>{t("picking_card_ready")}</span>
                                {" "}
                                <b>{putWall.items}</b> {t("picking_card_items")}
                                <span className="picking-choose-ready-sep" aria-hidden="true">·</span>
                                <b>{putWall.orders}</b> {t("picking_card_orders")}
                            </div>
                        ) : (
                            <div className="picking-choose-ready picking-choose-reason" role="note">
                                {putWall ? t("picking_put_wall_empty") : " "}
                            </div>
                        )}
                    </button>
                    <div className="picking-choose-footer" />
                </div>

                <div
                    data-testid="choose-sorting"
                    className={`picking-choose-tile picking-choose-tile--sorting${sortingEmpty ? " picking-choose-tile--disabled" : ""}`}
                >
                    <button
                        type="button"
                        data-testid="choose-sorting-tap"
                        className="picking-choose-tap"
                        disabled={busy || sortingEmpty}
                        onClick={() => !busy && !sortingEmpty && startWave("pick_and_sort")}
                    >
                        <div className="picking-choose-head">
                            <span className="picking-choose-icon"><Layers size={28} strokeWidth={1.8} aria-hidden="true" /></span>
                            <span className="picking-choose-name">{t("picking_wave_chooser_sorting")}</span>
                        </div>
                        <div className="picking-choose-stats" data-testid="sorting-next">
                            <div className="picking-choose-caption">{t("picking_card_next_wave")}</div>
                            <div className="picking-choose-stat">
                                <span className="picking-choose-stat-num">{sortingItems}</span>
                                <span className="picking-choose-stat-label">{t("picking_card_items")}</span>
                            </div>
                            <div className="picking-choose-stat">
                                {/* An older BE sends no order count: keep the
                                    cell so both tiles stay the same shape. */}
                                <span className="picking-choose-stat-num">{sortingOrders ?? "—"}</span>
                                <span className="picking-choose-stat-label">{t("picking_card_orders")}</span>
                            </div>
                        </div>
                        <div className="picking-choose-ready" data-testid="sorting-pool">
                            {sortingPool !== null ? (
                                <>
                                    <span>{t("picking_card_ready")}</span>
                                    {" "}
                                    <b>{sortingPool.items}</b> {t("picking_card_items")}
                                    <span className="picking-choose-ready-sep" aria-hidden="true">·</span>
                                    <b>{sortingPool.orders}</b> {t("picking_card_orders")}
                                </>
                            ) : " "}
                        </div>
                    </button>
                    <div className="picking-choose-footer">
                        {pasLarge?.available && (
                            <div className="picking-wave-size-row" role="radiogroup" aria-label={t("picking_wave_size")}>
                                <span className="picking-wave-size-label">{t("picking_wave_size")}</span>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={waveSize === "small"}
                                    className={`picking-wave-size-pill${waveSize === "small" ? " picking-wave-size-pill--active" : ""}`}
                                    disabled={busy || !pasLarge.small_ready}
                                    onClick={() => persistWaveSize("small")}
                                    title={pasLarge.small_ready ? t("picking_wave_size_small") : t("picking_small_blocked")}
                                >
                                    {t("picking_wave_size_small")} · {pasLarge.small_cap}
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={waveSize === "large"}
                                    className={`picking-wave-size-pill${waveSize === "large" ? " picking-wave-size-pill--active" : ""}`}
                                    disabled={busy || !pasLarge.large_ready}
                                    onClick={() => persistWaveSize("large")}
                                    title={pasLarge.large_ready ? t("picking_wave_size_large") : t("picking_large_too_few")}
                                >
                                    {t("picking_wave_size_large")} · {pasLarge.max_orders ?? ""}
                                </button>
                            </div>
                        )}
                        <label className="picking-choose-include">
                            <input
                                type="checkbox"
                                checked={includePutWall === true}
                                disabled={busy || includePutWall === null}
                                onChange={e => setIncludePutWall(e.target.checked)}
                            />
                            <span className="picking-choose-include-text">{t("picking_include_put_wall")}</span>
                        </label>
                    </div>
                </div>
            </div>
            {busy && <WaveStartingOverlay />}
        </div>
    );
};

export default PickingChooseWave;
